import React, { useState, useEffect } from 'react';

const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');

const PRIORITY_COLORS = { low: '#10b981', medium: '#f59e0b', high: '#ef4444' };
const STATUS_COLORS = { open: '#6366f1', in_progress: '#f59e0b', done: '#10b981' };
const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', done: 'Done' };

export default function MyTasks({ token, role, onStartLesson }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false); // NEW: tab state

  const fetchTasks = () => {
    if (!token) return;
    fetch(`${API_URL}/my/tasks`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) setTasks(data.tasks);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchTasks(); }, [token]); // eslint-disable-line

  // Refresh when lesson generation links a task
  useEffect(() => {
    const handler = () => fetchTasks();
    window.addEventListener('myTasksRefresh', handler);
    return () => window.removeEventListener('myTasksRefresh', handler);
  }, [token]); // eslint-disable-line

  // NEW: mark a task done locally + call backend
  const markDone = async (backlogId) => {
    // Optimistic update so UI responds instantly
    setTasks(prev => prev.map(t =>
      t.backlog_id === backlogId ? { ...t, status: 'done' } : t
    ));
    try {
      await fetch(`${API_URL}/my/tasks/${backlogId}/mark-done`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.error('Mark done failed:', e);
      // Revert on failure
      setTasks(prev => prev.map(t =>
        t.backlog_id === backlogId ? { ...t, status: 'in_progress' } : t
      ));
    }
  };

  if (!role) return null;
  if (tasks.length === 0 && !loading) return null;

  // NEW: split tasks into two buckets
  const openTasks = tasks.filter(t => t.status !== 'done');
  const doneTasks = tasks.filter(t => t.status === 'done');
  const visibleTasks = showCompleted ? doneTasks : openTasks;

  return (
    <div style={{
      position: 'fixed', bottom: '24px', left: '80px', zIndex: 200,
      width: collapsed ? '48px' : '340px',
      transition: 'width 0.2s ease',
    }}>
      {collapsed ? (
        <button
          onClick={() => setCollapsed(false)}
          style={{
            width: '48px', height: '48px', borderRadius: '50%',
            background: '#6366f1', border: 'none', cursor: 'pointer',
            color: '#fff', fontSize: '20px',
            boxShadow: '0 4px 12px rgba(99,102,241,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative',
          }}
          title="My Tasks"
        >
          📋
          {/* NEW: badge only counts open tasks */}
          {openTasks.length > 0 && (
            <span style={{
              position: 'absolute', top: -4, right: -4,
              background: '#ef4444', color: '#fff', borderRadius: '50%',
              width: '18px', height: '18px', fontSize: '10px', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {openTasks.length}
            </span>
          )}
        </button>
      ) : (
        <div style={{
          background: '#fff', borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)', overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            background: '#6366f1', padding: '14px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontWeight: 700, color: '#fff', fontSize: '14px' }}>📋 My Tasks</div>
              {/* NEW: shows open vs completed count */}
              <div style={{ fontSize: '11px', color: '#c7d2fe' }}>
                {openTasks.length} open · {doneTasks.length} completed
              </div>
            </div>
            <button
              onClick={() => setCollapsed(true)}
              style={{ background: 'none', border: 'none', color: '#c7d2fe', cursor: 'pointer', fontSize: '16px' }}
            >✕</button>
          </div>

          {/* NEW: Open / Completed tab bar */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <button
              onClick={() => setShowCompleted(false)}
              style={{
                flex: 1, padding: '8px', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: 600,
                background: !showCompleted ? '#fff' : 'transparent',
                color: !showCompleted ? '#6366f1' : '#94a3b8',
                borderBottom: !showCompleted ? '2px solid #6366f1' : '2px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              Open{openTasks.length > 0 && (
                <span style={{
                  background: '#6366f1', color: '#fff', borderRadius: '20px',
                  padding: '1px 6px', fontSize: '10px', marginLeft: '4px',
                }}>{openTasks.length}</span>
              )}
            </button>
            <button
              onClick={() => setShowCompleted(true)}
              style={{
                flex: 1, padding: '8px', border: 'none', cursor: 'pointer',
                fontSize: '12px', fontWeight: 600,
                background: showCompleted ? '#fff' : 'transparent',
                color: showCompleted ? '#10b981' : '#94a3b8',
                borderBottom: showCompleted ? '2px solid #10b981' : '2px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              Completed{doneTasks.length > 0 && (
                <span style={{
                  background: '#10b981', color: '#fff', borderRadius: '20px',
                  padding: '1px 6px', fontSize: '10px', marginLeft: '4px',
                }}>{doneTasks.length}</span>
              )}
            </button>
          </div>

          {/* Task list — now shows visibleTasks based on active tab */}
          <div style={{ maxHeight: '360px', overflowY: 'auto', padding: '8px' }}>
            {loading ? (
              <div style={{ padding: '16px', color: '#94a3b8', textAlign: 'center', fontSize: '13px' }}>Loading...</div>
            ) : visibleTasks.length === 0 ? (
              // NEW: context-aware empty states
              <div style={{ padding: '20px 16px', color: '#94a3b8', textAlign: 'center', fontSize: '13px' }}>
                {showCompleted ? '🎉 No completed tasks yet.' : '✅ All caught up — no open tasks!'}
              </div>
            ) : (
              visibleTasks.map(task => (
                <div key={task.backlog_id} style={{
                  // NEW: green tint + reduced opacity for done tasks
                  background: task.status === 'done' ? '#f0fdf4' : '#f8fafc',
                  borderRadius: '10px', padding: '12px 14px', marginBottom: '8px',
                  borderLeft: `3px solid ${task.status === 'done' ? '#10b981' : (PRIORITY_COLORS[task.priority] || '#94a3b8')}`,
                  opacity: task.status === 'done' ? 0.85 : 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>

                      // In the task card, replace the title div block:
<div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
  {task.status === 'done' && <span style={{ fontSize: '12px' }}>✅</span>}
  
  {/* NEW: assessment badge */}
  {task.is_assessment && (
    <span style={{
      fontSize: '9px', fontWeight: 700, padding: '1px 5px',
      borderRadius: '20px', background: '#fef3c7', color: '#d97706',
      border: '1px solid #fcd34d', whiteSpace: 'nowrap',
    }}>
      📝 ASSESSMENT
    </span>
  )}

  <div style={{
    fontWeight: 700, fontSize: '13px', color: '#0f172a',
    textDecoration: task.status === 'done' ? 'line-through' : 'none',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  }}>
    {task.title}
  </div>
</div>

// Also show assessment source hint under description:
{task.is_assessment && task.status !== 'done' && (
  <div style={{ fontSize: '11px', color: '#d97706', marginBottom: '4px' }}>
    {task.assessment_source === 'template_ids'
      ? `📚 Based on: ${(task.assessment_template_ids || []).join(', ') || 'linked lessons'}`
      : `🎯 Topic: ${task.assessment_topic || task.title}`
    }
    {task.question_count && ` · ${task.question_count} questions`}
  </div>
)}

                      {task.my_role && (
                        <span style={{
                          fontSize: '10px', fontWeight: 700, padding: '2px 6px',
                          borderRadius: '20px', background: '#eef2ff', color: '#6366f1',
                          marginBottom: '4px', display: 'inline-block',
                        }}>
                          {task.my_role.replace('_', ' ')}
                        </span>
                      )}

                      {task.description && (
                        <div style={{
                          fontSize: '12px', color: '#64748b', marginBottom: '6px',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {task.description}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '20px',
                          background: (PRIORITY_COLORS[task.priority] || '#94a3b8') + '20',
                          color: PRIORITY_COLORS[task.priority] || '#94a3b8',
                        }}>
                          {task.priority}
                        </span>
                        <span style={{
                          fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '20px',
                          background: (STATUS_COLORS[task.status] || '#94a3b8') + '20',
                          color: STATUS_COLORS[task.status] || '#94a3b8',
                        }}>
                          {STATUS_LABELS[task.status] || task.status}
                        </span>
                        {task.due_date && (
                          <span style={{ fontSize: '10px', color: '#94a3b8' }}>📅 {task.due_date}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action buttons — only shown on open/in_progress tasks */}
                  {task.status !== 'done' && (
                    <>
                      {/* Creator without a lesson yet: show Start Lesson */}
                      {task.my_role === 'creator' && !task.lesson_id && (
                        <button
                          onClick={() => onStartLesson(task)}
                          style={{
                            width: '100%', marginTop: '10px', padding: '7px',
                            background: '#6366f1', color: '#fff', border: 'none',
                            borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          🚀 Start Lesson
                        </button>
                      )}
                      {/* NEW: all other roles get Mark as Done */}
                      {task.my_role !== 'creator' && (
                        <button
                          onClick={() => markDone(task.backlog_id)}
                          style={{
                            width: '100%', marginTop: '10px', padding: '7px',
                            background: '#10b981', color: '#fff', border: 'none',
                            borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                          }}
                        >
                          ✅ Mark as Done
                        </button>
                      )}
                    </>
                  )}

                  {/* Linked lesson indicator (open tasks only) */}
                  {task.lesson_id && task.status !== 'done' && (
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#10b981', fontWeight: 600 }}>
                      ✓ Linked to {task.lesson_id}
                    </div>
                  )}

                  {/* NEW: completion footer for done tasks */}
                  {task.status === 'done' && (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#10b981', fontWeight: 600 }}>
                      ✓ Completed{task.lesson_id ? ` · ${task.lesson_id}` : ''}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
